import { Component, inject } from '@angular/core'
import { RouterOutlet, NavigationEnd, Router } from '@angular/router'
import { Title, Meta } from '@angular/platform-browser'
import { TranslateService } from '@ngx-translate/core'
import { HeaderComponent } from './core/header/header.component'
import { FooterComponent } from './core/footer/footer.component'
import { PreferencesService } from './services/preferences.service'
import { filter } from 'rxjs/operators'

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private translate = inject(TranslateService)
  private preferences = inject(PreferencesService)
  private titleService = inject(Title)
  private metaService = inject(Meta)
  private router = inject(Router)

  // Canonical base URL for production (updated per route).
  private readonly BASE_URL = 'https://www.sos-corp.fr'

  // Map route paths → translation key prefix in seo.*
  private readonly ROUTE_SEO_KEYS: Record<string, string> = {
    '/home': 'seo.home',
    '/services': 'seo.services',
    '/about': 'seo.about',
    '/portfolio': 'seo.portfolio',
    '/contact': 'seo.contact',
  }

  constructor() {
    // Load user preferences
    const savedLanguage = this.preferences.getLanguage()

    // Initialize translation service with saved language
    this.translate.setDefaultLang('fr')
    this.translate.use(savedLanguage)
    console.log(`✅ Translation service initialized with ${savedLanguage}`)
    console.log(`💾 User preferences loaded from localStorage`)

    // Update page title and meta tags when language changes
    this.translate.onLangChange.subscribe(() => {
      this.updateMetaTags()
    })

    // Update page title and meta tags on route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateMetaTags()
      })

    // Initial meta tags update
    this.updateMetaTags()
  }

  /**
   * Resolve the route-specific SEO translation key prefix.
   * Falls back to the global `seo.*` keys if the current path isn't mapped.
   */
  private getRouteSeoKey(field: 'title' | 'description' | 'og_title'): string {
    const path = this.router.url.split('?')[0].split('#')[0]
    const prefix = this.ROUTE_SEO_KEYS[path]
    return prefix ? `${prefix}.${field}` : `seo.${field}`
  }

  private updateMetaTags(): void {
    const titleKey = this.getRouteSeoKey('title')
    const descKey = this.getRouteSeoKey('description')
    const ogTitleKey = this.getRouteSeoKey('og_title')

    // Page title — route-specific first, falls back to global
    this.translate.get([titleKey, 'seo.title']).subscribe((res) => {
      const title = res[titleKey] !== titleKey ? res[titleKey] : res['seo.title']
      this.titleService.setTitle(title)
    })

    // Meta description — route-specific first
    this.translate.get([descKey, 'seo.description']).subscribe((res) => {
      const description =
        res[descKey] !== descKey ? res[descKey] : res['seo.description']
      this.metaService.updateTag({ name: 'description', content: description })
      this.metaService.updateTag({
        property: 'og:description',
        content: description,
      })
      this.metaService.updateTag({
        name: 'twitter:description',
        content: description,
      })
    })

    // Meta keywords — kept global (keywords are less route-specific by nature)
    this.translate.get('seo.keywords').subscribe((keywords: string) => {
      this.metaService.updateTag({ name: 'keywords', content: keywords })
    })

    // Open Graph / Twitter title — route-specific
    this.translate.get([ogTitleKey, 'seo.og_title']).subscribe((res) => {
      const ogTitle =
        res[ogTitleKey] !== ogTitleKey ? res[ogTitleKey] : res['seo.og_title']
      this.metaService.updateTag({ property: 'og:title', content: ogTitle })
      this.metaService.updateTag({ name: 'twitter:title', content: ogTitle })
    })

    // Language + locale
    const currentLang = this.translate.currentLang || 'fr'
    document.documentElement.lang = currentLang
    this.metaService.updateTag({
      name: 'language',
      content: currentLang === 'fr' ? 'French' : 'English',
    })
    this.metaService.updateTag({
      property: 'og:locale',
      content: currentLang === 'fr' ? 'fr_FR' : 'en_US',
    })

    // Canonical + hreflang (self-referential + alt language)
    this.updateCanonicalAndAlternates(currentLang)

    // og:url always reflects the current route
    const currentUrl = this.BASE_URL + this.router.url.split('?')[0]
    this.metaService.updateTag({ property: 'og:url', content: currentUrl })
    this.metaService.updateTag({ name: 'twitter:url', content: currentUrl })
  }

  /**
   * Maintain a single <link rel="canonical"> and <link rel="alternate">
   * elements reflecting the current route + language.
   */
  private updateCanonicalAndAlternates(currentLang: string): void {
    const head = document.head
    const path = this.router.url.split('?')[0]
    const canonical = `${this.BASE_URL}${path}`

    this.setOrUpdateLink('canonical', canonical)
    // hreflang alternates (same path — the app is rendered in the chosen lang)
    this.setOrUpdateLink('alternate', canonical, 'fr')
    this.setOrUpdateLink('alternate', canonical, 'en')
    this.setOrUpdateLink('alternate', canonical, 'x-default')
  }

  private setOrUpdateLink(
    rel: string,
    href: string,
    hreflang?: string,
  ): void {
    const selector = hreflang
      ? `link[rel="${rel}"][hreflang="${hreflang}"]`
      : `link[rel="${rel}"]:not([hreflang])`
    let link = document.head.querySelector<HTMLLinkElement>(selector)
    if (!link) {
      link = document.createElement('link')
      link.rel = rel
      if (hreflang) link.setAttribute('hreflang', hreflang)
      document.head.appendChild(link)
    }
    link.href = href
  }
}
